import { LoaderFunction } from 'react-router-dom';
import { of, take, zipWith } from 'rxjs';

import {
  companyConfigService,
  TCompanyConfigs,
} from '../../../../shared/services/companyConfig/companyConfig.service.ts';
import { ITabState, PrintFormsTabs } from '../states/tabState.model.ts';
import { printFormsTabLoadingService } from '../printFormsTabLoading.service.ts';
import {
  printFormsService,
  ReportsConfigurationRes,
  DataForReportsConfigurationPreviewRes,
  ListReportPrinterMappingsQueryRes,
} from '../services/printForms.service.ts';
import { responseHandler } from '../../../../shared/responseHandler/responseHandler.ts';
import {
  configsData,
  DictCompanyPrintersQueryRes,
  DictSubCompaniesRes,
  DictTranslationLanguagesQueryRes,
} from '../../../../shared/services/configsData/configsData.service.ts';

export const printFormsTabLoader: LoaderFunction = async ({ params }) => {
  const { tab } = params || {};
  if (!tab || !Object.values(PrintFormsTabs).includes(tab as PrintFormsTabs)) {
    location.href = '/404';
  }

  let tabState: Partial<ITabState> = {};

  switch (tab) {
    case PrintFormsTabs.general: {
      const res = await getConfigData();
      const { companyNameInReports } = res as TCompanyConfigs;
      tabState = { companyNameInReports };
      break;
    }
    case PrintFormsTabs.reportConfiguration: {
      const [inputDataForReportsPreview, reportConfiguration, subCompaniesOptions] =
        await Promise.all([
          printFormsService.globHelpers.streamToPromise(
            printFormsService.sub.getDataForReportsConfigurationPreview().pipe(
              responseHandler<DataForReportsConfigurationPreviewRes>({
                errorReturnType: undefined,
              }),
              take(1),
            ),
            () => printFormsService.pub.getDataForReportsConfigurationPreview(),
          ),
          printFormsService.globHelpers.streamToPromise(
            printFormsService.sub
              .getReportsConfiguration()
              .pipe(
                responseHandler<ReportsConfigurationRes>({ errorReturnType: undefined }),
                take(1),
              ),
            () => printFormsService.pub.getReportsConfiguration(),
          ),
          printFormsService.globHelpers.streamToPromise(
            configsData.sub
              .dictSubCompanies()
              .pipe(responseHandler<DictSubCompaniesRes>({ errorReturnType: [] }), take(1)),
            () => configsData.pub.common(['dictSubCompanies']),
          ),
        ]);
      tabState = { inputDataForReportsPreview, reportConfiguration, subCompaniesOptions };
      break;
    }
    case PrintFormsTabs.printersAssignment: {
      /*
        The tab itself is hidden when printing is unavailable (printFormsDetails.tsx), but this
        loader still runs for a bookmarked or hand-typed URL — and the printer dictionary is
        zipped in here without a responseHandler, so for a company with no PrintNode child
        account the 500 ("Child Account not found") took the whole loader down (WA-3777).
        Only an explicit `false` skips it; see formsTab.resolver.ts for the undefined case.
      */
      const skipCompanyPrinters =
        companyConfigService.getCachedConfigs()?.isPrintingAvailable === false;
      const [listReportPrinterMappings, companyPrinters] =
        await printFormsService.globHelpers.streamToPromise(
          printFormsService.sub.listReportPrinterMappings().pipe(
            responseHandler<ListReportPrinterMappingsQueryRes>({
              errorReturnType: [],
            }),
            zipWith(
              skipCompanyPrinters
                ? of<DictCompanyPrintersQueryRes>([])
                : configsData.sub.dictCompanyPrinters(),
            ),
          ),
          () => {
            printFormsService.pub.listReportPrinterMappings();
            if (!skipCompanyPrinters) {
              configsData.pub.common(['dictCompanyPrinters'], true);
            }
          },
        );
      tabState = { listReportPrinterMappings, companyPrinters };
      break;
    }
    case PrintFormsTabs.email: {
      const [res, translationLanguages] = await Promise.all([
        getConfigData() as Promise<TCompanyConfigs>,
        printFormsService.globHelpers.streamToPromise(
          configsData.sub
            .dictTranslationLanguages()
            .pipe(
              responseHandler<DictTranslationLanguagesQueryRes>({ errorReturnType: [] }),
              take(1),
            ),
          () => configsData.pub.common(['dictTranslationLanguages']),
        ),
      ]);
      tabState = {
        isDefaultEmailTextForInvoice: res.isDefaultEmailTextForInvoice,
        customEmailTextForInvoice: res.customEmailTextForInvoice,
        isDefaultEmailTextForDebtor: res.isDefaultEmailTextForDebtor,
        customEmailTextForDebtor: res.customEmailTextForDebtor,
        isDefaultEmailTextForOrder: res.isDefaultEmailTextForOrder,
        customEmailTextForOrder: res.customEmailTextForOrder,
        isDefaultEmailTextForOffer: res.isDefaultEmailTextForOffer,
        customEmailTextForOffer: res.customEmailTextForOffer,
        orderEmailTextTranslations: res.orderEmailTextTranslations ?? [],
        offerEmailTextTranslations: res.offerEmailTextTranslations ?? [],
        invoiceEmailTextTranslations: res.invoiceEmailTextTranslations ?? [],
        debtReminderEmailTextTranslations: res.debtReminderEmailTextTranslations ?? [],
        translationLanguages: translationLanguages ?? [],
      };
      break;
    }
    default:
      tabState = {};
      break;
  }
  printFormsTabLoadingService.pub.loading(false);
  return tabState;
};

const getConfigData = async () => {
  return new Promise((res) => companyConfigService.getConfigs(true).pipe(take(1)).subscribe(res));
};
